import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useHeaderAddon } from '@hooks/useHeaderContent';
import { SegmentedTabs } from '@skupreme/shared/ui';
import { useIsV2Account } from '@features/page-versions';
import { getOrderTypeFromPath, getOrderTypeTabs } from '../../lib/type-tabs';

export type OrderTypeTabsProps = {
  /** Sections this brand does not sell, from its configuration. */
  hiddenSections?: string[];
  className?: string;
};

/** D2C against B2B — the switch between the two order books. */
export function OrderTypeTabs({
  hiddenSections,
  className,
}: OrderTypeTabsProps) {
  const location = useLocation();

  return (
    <SegmentedTabs
      label="Order type"
      items={getOrderTypeTabs(hiddenSections)}
      selectedKey={getOrderTypeFromPath(location.pathname)}
      className={className}
    />
  );
}

/**
 * The D2C/B2B switch, next to the page title in the app header. A V2 account
 * has no B2B book (wholesale invoices are legacy), and a switch with one side
 * left is not drawn.
 */
export function useOrderTypeTabsInHeader(hiddenSections?: string[]) {
  const isV2Account = useIsV2Account();
  /* The brand's configuration: one array for the life of the app. */
  const tabs = useMemo(() => {
    const hidden = isV2Account
      ? [...(hiddenSections ?? []), 'B2B']
      : hiddenSections;

    return getOrderTypeTabs(hidden).length > 1 ? (
      <OrderTypeTabs hiddenSections={hidden} />
    ) : null;
  }, [hiddenSections, isV2Account]);

  useHeaderAddon(tabs);
}
